// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * What the estate has analysed, and what came of it — one row per course.
 *
 * The per-course panel answers "how did this course go" and the fleet page answers "what have
 * the machines been doing". This is the third question, which had no home: what have we
 * analysed at all, and what do those courses hold now.
 */

/** One course's analysis history and current features. */
export type AnalysisOverviewRow = {
  courseId: string;
  courseName: string;
  /** When an ordinary run last succeeded; null for a course never analysed successfully. */
  lastAnalysedAt: string | null;
  /** When a run last started, successful or not. */
  lastStartedAt: string | null;
  /** When analysis was last asked for. Set with no run behind it means queued. */
  requestedAt: string | null;
  runs: number;
  failedRuns: number;
  /** Comparison runs against a trained model. They publish nothing. */
  modelRuns: number;
  features: number;
  /** Of `features`, how many an analysis produced rather than OSM, GPS or a person. */
  satelliteFeatures: number;
  pendingReview: number;
  accepted: number;
};

export type AnalysisOverviewState =
  | { status: 'loading' }
  | { status: 'ready'; loadedAt: number; courses: AnalysisOverviewRow[] }
  | { status: 'forbidden' }
  | { status: 'failed' };

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

export const mapAnalysisOverview = (body: unknown): AnalysisOverviewRow[] => {
  const root = (body ?? {}) as Row;
  const courses = root.courses ?? root.Courses;
  if (!Array.isArray(courses)) return [];

  return (courses as Row[])
    .map((row) => ({
      courseId: str(row, 'courseId', 'CourseId') ?? '',
      courseName: str(row, 'courseName', 'CourseName') ?? '',
      lastAnalysedAt: str(row, 'lastAnalysedAt', 'LastAnalysedAt'),
      lastStartedAt: str(row, 'lastStartedAt', 'LastStartedAt'),
      requestedAt: str(row, 'requestedAt', 'RequestedAt'),
      runs: num(row, 'runs', 'Runs'),
      failedRuns: num(row, 'failedRuns', 'FailedRuns'),
      modelRuns: num(row, 'modelRuns', 'ModelRuns'),
      features: num(row, 'features', 'Features'),
      satelliteFeatures: num(row, 'satelliteFeatures', 'SatelliteFeatures'),
      pendingReview: num(row, 'pendingReview', 'PendingReview'),
      accepted: num(row, 'accepted', 'Accepted'),
    }))
    .filter((row) => row.courseId.length > 0);
};

/** Totals across every course, for the header line. */
export const overviewTotals = (courses: AnalysisOverviewRow[]) => ({
  courses: courses.length,
  analysed: courses.filter((c) => c.lastAnalysedAt !== null).length,
  features: courses.reduce((sum, c) => sum + c.features, 0),
  satelliteFeatures: courses.reduce((sum, c) => sum + c.satelliteFeatures, 0),
  pendingReview: courses.reduce((sum, c) => sum + c.pendingReview, 0),
});

export function useAnalysisOverview(
  apiBaseUrl: string,
  token: string | null | undefined,
  enabled: boolean
): { state: AnalysisOverviewState; refresh: () => void } {
  const [state, setState] = useState<AnalysisOverviewState>({ status: 'loading' });
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, []);

  useEffect(() => {
    if (!enabled || !token) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/analysis-overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed' });
          return;
        }

        const courses = mapAnalysisOverview(await response.json());
        if (cancelled) return;
        setState({ status: 'ready', loadedAt: Date.now(), courses });
      } catch {
        if (!cancelled) setState({ status: 'failed' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // No poll loop: an analysis takes minutes and this is a page somebody reads, not a
    // dashboard that has to stay live. The refresh button covers the rest.
  }, [apiBaseUrl, token, enabled, reloadKey]);

  return { state, refresh };
}
